/** Application-wide providers: theme, query client, snackbars, auth. */
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import CloseRounded from '@mui/icons-material/CloseRounded';
import { Box, CssBaseline, IconButton, Snackbar, ThemeProvider } from '@mui/material';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import { ApiError } from '@/api/client';
import { AuthProvider } from '@/auth/AuthProvider';
import { ColorModeProvider, useColorMode } from '@/theme/colorMode';
import { buildTheme, motion, radius, semantic, status, surface } from '@/theme/theme';

type Severity = 'success' | 'error' | 'info' | 'warning';

interface Notice {
  message: string;
  severity: Severity;
  correlationId?: string | undefined;
}

interface NoticeContextValue {
  notify: (message: string, severity?: Severity) => void;
  /** Renders an ApiError as a snackbar, keeping its correlation ID for support. */
  notifyError: (error: unknown, fallback?: string) => void;
}

const NoticeContext = createContext<NoticeContextValue | null>(null);

export function useNotice(): NoticeContextValue {
  const context = useContext(NoticeContext);
  if (!context) throw new Error('useNotice must be used inside the app providers.');
  return context;
}

/**
 * The snackbar. Exported so a test can render the real one: what `notifyError` puts on screen for
 * an `ApiError` — the API's own sentence, not a fallback — is the behaviour a refusal screen is
 * for, and a mocked `useNotice` cannot show it.
 */
export function NoticeProvider({ children }: { children: ReactNode }): JSX.Element {
  const [notice, setNotice] = useState<Notice | null>(null);

  const notify = useCallback((message: string, severity: Severity = 'success') => {
    setNotice({ message, severity });
  }, []);

  const notifyError = useCallback((error: unknown, fallback = 'That did not work.') => {
    if (error instanceof ApiError) {
      setNotice({
        message: error.message,
        severity: 'error',
        correlationId: error.correlationId,
      });
      return;
    }
    setNotice({ message: fallback, severity: 'error' });
  }, []);

  const value = useMemo(() => ({ notify, notifyError }), [notify, notifyError]);

  return (
    <NoticeContext.Provider value={value}>
      {children}
      {/* The guide's toast: an ink pill at the bottom centre with a status dot. A result clears
          itself after 3.5 seconds; an error stays until it is closed, because it has to be read. */}
      <Snackbar
        open={notice !== null}
        autoHideDuration={notice?.severity === 'error' ? null : 3500}
        onClose={(_, reason) => {
          if (reason === 'clickaway' && notice?.severity === 'error') return;
          setNotice(null);
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {notice ? (
          <Box
            role={notice.severity === 'error' ? 'alert' : 'status'}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              maxWidth: 560,
              px: 5,
              py: 2.5,
              borderRadius: radius.pill,
              backgroundColor: surface.inverse,
              color: semantic.inkOnDark,
              fontSize: '0.875rem',
              fontWeight: 500,
              lineHeight: 1.45,
              animation: `toast-in ${motion.base} ${motion.enter}`,
              '@keyframes toast-in': {
                from: { opacity: 0, transform: 'translateY(8px)' },
                to: { opacity: 1, transform: 'none' },
              },
            }}
          >
            <Box
              component="span"
              aria-hidden
              sx={{
                width: '0.5rem',
                height: '0.5rem',
                borderRadius: '50%',
                flexShrink: 0,
                backgroundColor: {
                  success: status.good,
                  error: status.critical,
                  warning: status.serious,
                  info: semantic.interactive,
                }[notice.severity],
              }}
            />
            <Box component="span">
              {notice.message}
              {notice.correlationId ? (
                <Box component="span" sx={{ display: 'block', opacity: 0.72, fontSize: '0.8125rem' }}>
                  Reference {notice.correlationId.slice(0, 8)}
                </Box>
              ) : null}
            </Box>
            {notice.severity === 'error' ? (
              <IconButton
                size="small"
                aria-label="Close"
                onClick={() => setNotice(null)}
                sx={{ color: 'inherit', ml: 1, minHeight: 0 }}
              >
                <CloseRounded fontSize="small" />
              </IconButton>
            ) : null}
          </Box>
        ) : (
          <span />
        )}
      </Snackbar>
    </NoticeContext.Provider>
  );
}

function makeQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        gcTime: 5 * 60_000,
        refetchOnWindowFocus: false,
        retry: (failureCount, error) => {
          // A 4xx will not become a 2xx by asking again.
          if (error instanceof ApiError && error.status < 500) return false;
          return failureCount < 2;
        },
      },
      mutations: { retry: false },
    },
  });
}

/**
 * The theme follows the resolved colour mode.
 *
 * `tokens.css` has already switched the custom properties by the time this runs — this rebuilds the
 * Material UI palette, which is the half that cannot read them.
 */
function ThemedApp({ children }: { children: ReactNode }): JSX.Element {
  const { mode } = useColorMode();
  const theme = useMemo(() => buildTheme(mode), [mode]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline enableColorScheme />
      {children}
    </ThemeProvider>
  );
}

export function AppProviders({ children }: { children: ReactNode }): JSX.Element {
  const [queryClient] = useState(makeQueryClient);

  return (
    <QueryClientProvider client={queryClient}>
      <ColorModeProvider>
        <ThemedApp>
          <NoticeProvider>
            <AuthProvider>{children}</AuthProvider>
          </NoticeProvider>
        </ThemedApp>
      </ColorModeProvider>
    </QueryClientProvider>
  );
}
